import React, { useState, useEffect } from 'react';
import {
    Box, VStack, HStack, Text, Heading,
    Icon, SimpleGrid, Card, CardBody, Badge,
    Flex, useColorModeValue, Button
} from '@chakra-ui/react';
import { IoCarSportOutline, IoBookOutline, IoCalendarClearOutline, IoFastFoodOutline, IoPersonCircleOutline, IoReaderOutline } from 'react-icons/io5';
import { FiMapPin, FiCheckCircle, FiActivity, FiUser, FiMoreHorizontal, FiTruck, FiCalendar, FiCoffee } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import LogoPulseLoader from '../components/LogoPulseLoader';
import LoadingOverlay from '../components/LoadingOverlay';
import { useGPS } from '../context/GPSContext';
import AnnouncementBanner from '../components/parent/AnnouncementBanner';
import { getUnionFeatures } from '../utils/institutionFeatures';
import useChatEnabled from '../mobile/hooks/useChatEnabled';

import { Badge as AppBadge } from '@capawesome/capacitor-badge';
import { motion } from 'framer-motion';

// Animation Variants
const containerVariants = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1
        }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
};

const MotionBox = motion(Box);
const MotionSimpleGrid = motion(SimpleGrid);
const MotionVStack = motion(VStack);

const ParentDashboard = () => {
    const navigate = useNavigate();
    const [error, setError] = useState(null);
    const [children, setChildren] = useState([]);
    const [loading, setLoading] = useState(true);
    const [unsignedCount, setUnsignedCount] = useState(0); // Badge Count
    const parentName = children.length > 0 ? children[0].parent_name : '家長';
    const bg = useColorModeValue('gray.50', 'gray.900');
    // [B3] 休眠出貨：全域旗標 off 時（prod 現況）維持原本導向聯絡簿頁，零可見變化。
    const chatFlagOn = useChatEnabled();

    const { syncTrackingStudents } = useGPS();

    const fetchData = async (isPolling = false) => {
        try {
            const res = await authAPI.getMe();
            const kids = res.data.children || [];
            const badgeCnt = res.data.unsigned_comm_books_cnt || 0;

            setChildren(kids);
            setUnsignedCount(badgeCnt);
            syncTrackingStudents(kids); // Sync GPS state

            // Sync App Icon Badge
            try {
                // Check and Request Permissions if needed
                const permResult = await AppBadge.checkPermissions();
                if (permResult.display !== 'granted') {
                    const reqResult = await AppBadge.requestPermissions();
                    if (reqResult.display !== 'granted') {
                        console.warn("Badge permission not granted");
                        return; // Stop if user denied
                    }
                }

                if (badgeCnt > 0) {
                    await AppBadge.set({ count: badgeCnt });
                } else {
                    await AppBadge.clear();
                }
            } catch (e) {
                console.warn("Badge sync failed:", e);
                // Ignore badge errors (e.g. web/desktop or unsupported launcher)
            }

            setError(null); // Clear error on success
        } catch (err) {
            console.error("Dashboard fetch error:", err);
            // Only set error state if we don't have data yet (initial load failed)
            if (!isPolling && children.length === 0) {
                setError(err);
            }
        } finally {
            if (!isPolling) setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // Poll every 30s
        const interval = setInterval(() => fetchData(true), 30000);

        // Listen for push notification refresh triggers
        const handleRefresh = () => {
            console.log("Push notification received, refreshing dashboard...");
            fetchData(true);
        };
        window.addEventListener('data-refresh', handleRefresh);

        return () => {
            clearInterval(interval);
            window.removeEventListener('data-refresh', handleRefresh);
        };
    }, []);

    const handleRetry = () => {
        setLoading(true);
        setError(null);
        fetchData();
    };

    if (loading) {
        return (
            <Flex justify="center" align="center" h="50vh">
                <LogoPulseLoader />
            </Flex>
        );
    }

    if (error) {
        return (
            <Flex direction="column" align="center" justify="center" h="60vh" px={6} textAlign="center">
                <Icon as={FiActivity} boxSize={16} color="red.400" mb={4} />
                <Heading size="md" mb={2} color="onCanvas.500">無法連線到伺服器</Heading>
                <Text color="onCanvas.400" mb={6}>請檢查您的網路連線，或稍後再試。</Text>
                <Button onClick={handleRetry} colorScheme="brand" size="lg" rounded="xl">
                    重新嘗試
                </Button>
            </Flex>
        );
    }

    // [A2] 名下孩子機構 features 的聯集：天籟家庭（features 全開）五個服務按鈕逐字不變；
    // 單幼稚園孩子家庭（comm_book/leaves/lunch 關閉）只會看到「孩子接送」「基本資料」。
    const features = getUnionFeatures(children);
    // [B3] 同 MobileBottomNav：全域旗標 + 至少一孩子機構開 chat，兩者都通過才改導向對話列表。
    const chatReady = chatFlagOn && features.chat;

    return (
        <MotionVStack
            spacing={6}
            align="stretch"
            pb={4}
            variants={containerVariants}
            initial="hidden"
            animate="show"
        >
            {/* 1. Greeting & Billboard Section */}
            <MotionBox variants={itemVariants}>
                {/* Billboard (Banner) — 智慧公告輪播 */}
                <AnnouncementBanner />
            </MotionBox>

            {/* 2. Children Status Summary (Moved Up) */}
            <MotionBox variants={itemVariants}>
                <Text fontSize="lg" fontWeight="bold" color="onCanvas.500" mb={3} px={1}>
                    孩子狀態
                </Text>
                <VStack spacing={3}>
                    {children.map(child => (
                        <StatusCard key={child.id} child={child} />
                    ))}
                    {children.length === 0 && (
                        <Flex direction="column" align="center" justify="center" py={10} bg="gray.50" borderRadius="2xl">
                            <Icon as={FiActivity} boxSize={12} color="gray.300" mb={3} />
                            <Text color="gray.500" fontSize="md">目前暫無相關資料</Text>
                        </Flex>
                    )}
                </VStack>
            </MotionBox>

            {/* 3. Quick Services Grid */}
            <MotionBox variants={itemVariants}>
                <SimpleGrid columns={2} spacing={4}>
                    {features.pickup && (
                        <ServiceButton
                            icon={IoCarSportOutline}
                            label="孩子接送"
                            onClick={() => navigate('/pickup')}
                        />
                    )}
                    {features.comm_book && (
                        <ServiceButton
                            icon={IoReaderOutline}
                            label="聯絡簿"
                            onClick={() => navigate(chatReady ? '/parent/chat' : '/parent/communication-book')}
                            badgeCount={unsignedCount}
                        />
                    )}
                    {features.leaves && (
                        <ServiceButton
                            icon={IoCalendarClearOutline}
                            label="請假登記"
                            onClick={() => navigate('/parent/leaves')}
                        />
                    )}
                    {features.lunch && (
                        <ServiceButton
                            icon={IoFastFoodOutline}
                            label="訂餐登記"
                            onClick={() => navigate('/parent/lunch')}
                        />
                    )}
                    <ServiceButton
                        icon={IoPersonCircleOutline}
                        label="基本資料"
                        onClick={() => navigate('/parent/settings')}
                        color="cyan"
                    />
                </SimpleGrid>
            </MotionBox>
        </MotionVStack>
    );
};

// --- Sub-components ---

const ServiceButton = ({ icon, label, onClick, badgeCount }) => (
    <Box
        as="button"
        onClick={onClick}
        bg="gray.100"
        p={5}
        borderRadius="2xl"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        _active={{ opacity: 0.7, transform: "scale(0.98)" }}
        transition="all 0.1s"
        h="110px"
        w="100%"
        boxShadow="sm"
        border="1px solid"
        borderColor="gray.100"
        position="relative" // For Badge
    >
        {badgeCount > 0 && (
            <Flex
                position="absolute"
                top="10px"
                right="10px"
                bg="red.500"
                color="white"
                fontSize="xs"
                fontWeight="bold"
                borderRadius="full"
                w="20px"
                h="20px"
                align="center"
                justify="center"
                boxShadow="sm"
            >
                {badgeCount > 9 ? '9+' : badgeCount}
            </Flex>
        )}
        <VStack spacing={2}>
            <Icon as={icon} boxSize={9} color="brand.800" />
            <Text fontWeight="bold" fontSize="lg" color="brand.800">
                {label}
            </Text>
        </VStack>
    </Box>
);

// Reuse StatusCard Logic (Simplified for integration)
const StatusCard = ({ child }) => {
    const statusConfig = {
        'NOT_ARRIVED': { color: 'gray.500', label: '尚未到班', icon: FiActivity, bg: 'gray.100' },
        'ARRIVED': { color: 'blue.600', label: '已進班上課', icon: FiMapPin, bg: 'blue.50' },
        'HOMEWORK_PENDING': { color: 'orange.500', label: '課輔加強中', icon: FiCoffee, bg: 'orange.50' },
        'READY_FOR_PICKUP': { color: 'green.600', label: '可以接送了', icon: FiCheckCircle, bg: 'green.50' },
        'PARENT_EN_ROUTE': { color: 'red.600', label: '家長路途中', icon: FiTruck, bg: 'red.50' },
        'PICKUP_COMPLETED': { color: 'purple.600', label: '已放學回家', icon: FiUser, bg: 'purple.50' },
        'LEAVE': { color: 'yellow.500', label: '請假', icon: FiCalendar, bg: 'yellow.50' },
    }[child.status] || { color: 'gray.500', label: '狀態更新中', icon: FiMoreHorizontal, bg: 'gray.50' };

    const StatusIcon = statusConfig.icon;

    return (
        <HStack
            w="100%"
            bg="white"
            p={4}
            borderRadius="2xl"
            shadow="md"
            border="1px solid"
            borderColor="gray.200"
            spacing={4}
            borderLeft="6px solid"
            borderLeftColor={statusConfig.color}
        >
            <VStack align="start" spacing={1} flex={1}>
                <Text fontWeight="bold" fontSize="lg">{child.full_name}</Text>
                <Text fontSize="sm" color="gray.500">{child.class?.name}</Text>
            </VStack>
            <Badge bg={statusConfig.color} color="white" borderRadius="full" px={3} py={1} fontSize="sm" fontWeight="bold">
                {statusConfig.label}
            </Badge>
        </HStack>
    );
};

export default ParentDashboard;
